import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Avatar } from "./ui.jsx";
import NotificationBell from "./NotificationBell.jsx";
import api, { clearAuth } from "../api";
import { THEMES, applyTheme, storedTheme } from "../theme.js";
import { SCALES, applyScale, storedScale } from "../scale.js";
import { KITS, applyKit, storedKit } from "../skins.js";
import {
  HomeIcon,
  PlayCircleIcon,
  InsightsIcon,
  CoachingIcon,
  PlaylistIcon,
  ReportsIcon,
  SettingsIcon,
  LogoutIcon,
  BuildingIcon,
  TrendingUpIcon,
  ClockIcon,     // Switch-off 2027 (review 2026-09-21, switchoff)
  BookmarkIcon,  // Renewals (review 2026-09-21, renewals)
  MapPinIcon,    // Mobile coverage (review 2026-09-21, coverage)
  TrophyIcon,    // The Games, the rep's page ("Theirs", 2026-10-07)
} from "./Icons.jsx";

export default function Sidebar({ user }) {
  const [menuOpen, setMenuOpen] = useState(false);
  // Collapse to the narrow icon rail. Remembered per browser and applied as a class on <html>
  // (like the theme) so the content margin shifts too. useLayoutEffect = no flash on load.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("repiq_sidebar_collapsed") === "1"; } catch { return false; }
  });
  useLayoutEffect(() => {
    document.documentElement.classList.toggle("sidebar-collapsed", collapsed);
    try { localStorage.setItem("repiq_sidebar_collapsed", collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);
  const [theme, setTheme] = useState(user?.theme || storedTheme());
  const changeTheme = (id) => {
    setTheme(id);
    applyTheme(id);
    api.put("/api/auth/me/theme", { theme: id }).catch(() => {});
  };
  // Display size (Salem 2026-09-27). Auto fits the screen; a pinned size is per browser.
  const [scale, setScale] = useState(storedScale);
  const changeScale = (id) => { setScale(id); applyScale(id); };
  // Menu background (Salem 2026-08-27) — a Premier League kit behind the left menu. Remembered
  // in this browser only; it's a personal bit of fun, not a company setting, so it needs no
  // server round-trip and can't slow anything down.
  const [kit, setKit] = useState(storedKit);
  const changeKit = (id) => { setKit(id); applyKit(id); };
  const kitName = (KITS.find((k) => k.id === kit) || KITS[0]).name;
  const [companyLogo, setCompanyLogo] = useState(() => {
    try { return sessionStorage.getItem("repiq_company_logo") || null; } catch { return null; }
  });
  const navigate = useNavigate();
  const popRef = useRef(null);
  // Below 900px the sidebar is a top bar and the nav lives in a sheet (design system 1.0,
  // phase 3). The sheet closes on Esc, on navigation, and when the window grows past the
  // breakpoint again; while open, Tab stays inside it (a focus trap).
  const [sheetOpen, setSheetOpen] = useState(false);
  const sheetRef = useRef(null);
  const menuBtnRef = useRef(null);
  useEffect(() => {
    if (!sheetOpen) return;
    const first = sheetRef.current?.querySelector("a, button, select, input");
    first?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { setSheetOpen(false); menuBtnRef.current?.focus(); return; }
      if (e.key !== "Tab" || !sheetRef.current) return;
      const items = [...sheetRef.current.querySelectorAll("a, button, select, input")].filter((el) => !el.disabled && el.offsetParent !== null);
      if (!items.length) return;
      const [head, tail] = [items[0], items[items.length - 1]];
      if (e.shiftKey && document.activeElement === head) { e.preventDefault(); tail.focus(); }
      else if (!e.shiftKey && document.activeElement === tail) { e.preventDefault(); head.focus(); }
    };
    const onResize = () => { if (window.innerWidth >= 900) setSheetOpen(false); };
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
      document.body.style.overflow = "";
    };
  }, [sheetOpen]);

  // The company logo (set in Settings → Company) is shown as the app's small brand mark.
  useEffect(() => {
    api.get("/api/company").then((d) => {
      setCompanyLogo(d?.logo || null);
      try { d?.logo ? sessionStorage.setItem("repiq_company_logo", d.logo) : sessionStorage.removeItem("repiq_company_logo"); } catch { /* ignore */ }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const logout = () => {
    clearAuth();
    navigate("/login");
  };

  /* `badge` is the same red pill the bell wears. It hangs off the ICON, not the row, so it
     survives the sidebar being collapsed — collapsed is exactly when you have only the icon
     to go on, and it is the state a badge earns its keep in. */
  const link = (to, label, Icon, end = false, badge = 0) => (
    <NavLink key={to} to={to} end={end}
      title={badge > 0 ? `${label} — ${badge} waiting` : label}
      className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")}>
      <span className="sidebar-icon" aria-hidden="true" style={{ position: "relative" }}>
        <Icon size={20} />
        {badge > 0 && <span className="bell-badge">{badge > 9 ? "9+" : badge}</span>}
      </span>
      <span className="sidebar-label">{label}{badge > 0 ? ` (${badge})` : ""}</span>
    </NavLink>
  );

  const SunIcon = ({ size = 21 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
  const ChevronLeftIcon = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
  const GridIcon = ({ size = 21 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
  const UsersIcon = ({ size = 21 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
  const CalendarIcon = ({ size = 21 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
  const BoxIcon = ({ size = 21 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><path d="M3.27 6.96 12 12.01l8.73-5.05M12 22.08V12" />
    </svg>
  );
  const BarChartIcon = ({ size = 21 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="21" x2="21" y2="21" /><rect x="5" y="11" width="3.5" height="8" /><rect x="10.5" y="6" width="3.5" height="13" /><rect x="16" y="14" width="3.5" height="5" />
    </svg>
  );
  const isAdmin = user?.role === "admin";
  const isManagerOrAdmin = isAdmin || user?.sales_role === "manager";
  const isOps = user?.platform_role === "operations";

  // The role's landing page is the first thing in the nav (Today / Command Centre / Order Hub).
  const home = isOps
    ? { to: "/orders", label: "Order Hub", Icon: BoxIcon }
    : user?.sales_role === "manager"
      ? { to: "/command-centre", label: "Command Centre", Icon: GridIcon }
      : { to: "/today", label: "Today", Icon: SunIcon };

  // OG — the Oppsession Games (sales-floor gamification). Managers run the games from here;
  // it sits right under the landing page so it reads as a headline tab.
  const GamepadIcon = ({ size = 21 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 12h4M8 10v4M15 11h.01M18 13h.01" />
      <path d="M17.32 5H6.68a4 4 0 0 0-3.98 3.59c-.01.12-.02.25-.04.37-.24 1.97-.7 5.83-.7 7.04a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.41-1.41A2 2 0 0 1 9.79 16h4.42a2 2 0 0 1 1.42.59L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.21-.46-5.07-.7-7.04l-.04-.37A4 4 0 0 0 17.32 5z" />
    </svg>
  );

  // Lead Hub — the lead book (managers enter/edit; replaces the Excel Lead Tracker).
  const TargetIcon = ({ size = 21 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
    </svg>
  );

  // The Board — the pinboard: Top Table lunches, pool finals, birthday cakes. Everyone sees it.
  const PinIcon = ({ size = 21 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 17v5" />
      <path d="M9 10.5V4h6v6.5l2.5 3.5H6.5L9 10.5z" />
    </svg>
  );

  // The Academy (formerly TrainIQ) — training & development: the cap. Everyone sees it.
  const CapIcon = ({ size = 21 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 9L12 4 2 9l10 5 10-5z" />
      <path d="M6 11.5V16c0 1.5 2.7 3 6 3s6-1.5 6-3v-4.5" />
      <path d="M22 9v5" />
    </svg>
  );

  /* Conversations waiting — the sidebar badge (Salem 2026-09-04: *"It helps us know that
     there are messages arriving."*). Same cadence and the same manners as the bell: once a
     minute, nothing at all while the tab is hidden, and an immediate re-check when you come
     back to it. It also re-checks on every navigation, which is what makes the badge go DOWN
     the moment you have read a thread — a count that only ever climbs until the next poll
     would teach people to ignore it. */
  const [waWaiting, setWaWaiting] = useState(0);
  // Open missed calls and voicemails this person can see and act on — shown on CallIQ, which
  // is where they live (a tab between Recordings and Insights). The sidebar renders on every
  // screen, so this is the "visible without going into CallIQ" signal (Salem 2026-09-08).
  // Same polling contract as the WhatsApp badge — quiet when the tab is hidden, re-checked on
  // navigation so it goes DOWN the moment you have dealt with something.
  const [coverWaiting, setCoverWaiting] = useState(0);
  const location = useLocation();
  const loadWaiting = () => {
    if (document.visibilityState === "hidden") return;
    api.get("/api/v1/whatsapp/unread")
      .then((d) => setWaWaiting(d.threads || 0))
      .catch(() => {});          // a badge is never worth an error on screen
    api.get("/api/missed/count")
      .then((d) => setCoverWaiting(d.count || 0))
      .catch(() => {});
  };
  useEffect(() => {
    const t = setInterval(loadWaiting, 60_000);
    const onVis = () => { if (document.visibilityState === "visible") loadWaiting(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("repiq:wa-read", loadWaiting);
    window.addEventListener("repiq:missed-read", loadWaiting);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("repiq:wa-read", loadWaiting);
      window.removeEventListener("repiq:missed-read", loadWaiting);
    };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadWaiting(); setSheetOpen(false); }, [location.pathname]);   // eslint-disable-line react-hooks/exhaustive-deps

  // RepIQ Conversations — WhatsApp for Business. EVERYONE gets the inbox: reps their own
  // threads + the queue, Operations all threads (they run the orders), managers everything.
  const ChatIcon = ({ size = 21 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );

  // Ask Order Toolkit — the Sales Toolkit library (managers upload/audit; reps ask via the
  // floating dock, so only the LIBRARY needs a nav entry).
  const ToolboxIcon = ({ size = 21 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="12" rx="2" />
      <path d="M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 13h18M10 13v2h4v-2" />
    </svg>
  );

  // Every destination grouped by intent so the sidebar reads like one product, not a pile of icons.
  // A group renders only if it has at least one item the current role can see.
  const groups = [
    { items: [home, isManagerOrAdmin && { to: "/og", label: "OG — The Games", Icon: GamepadIcon }].filter(Boolean) },
    { label: "Sell", items: [
      !isOps && { to: "/salesiq", label: "SalesIQ", Icon: TrendingUpIcon },
      // My Leads (2026-08-25): the rep's own Lead Hub — funnel, pillar conversion, book.
      !isOps && !isManagerOrAdmin && { to: "/myleads", label: "My Leads", Icon: TargetIcon },
      !isOps && isManagerOrAdmin && { to: "/orders", label: "Order Hub", Icon: BoxIcon },
      !isOps && isManagerOrAdmin && { to: "/leads", label: "Lead Hub", Icon: TargetIcon },
      // Commit view (review 2026-09-21, managers — §7.3): where the quarter really stands, assumptions written on it.
      !isOps && isManagerOrAdmin && { to: "/commit", label: "Commit view", Icon: BarChartIcon },
      { to: "/companyiq", label: "CompanyIQ", Icon: BuildingIcon },
      // Renewals, New businesses, Switch-off 2027 and Mobile coverage moved OUT of this top-level
      // list and INTO CompanyIQ as tabs (Salem, 2026-09-25) - see components/CompanyIQTabs.jsx.
      // Their routes (/renewals, /companyiq/new-businesses, /switchoff, /coverage) are unchanged,
      // so nothing that links to them breaks; they're just reached via CompanyIQ now, not the nav.
      // My accounts (review 2026-09-21, retention — §6.1): a rep's existing customers with health; managers the floor.
      { to: "/accounts", label: isManagerOrAdmin ? "Accounts" : "My accounts", Icon: UsersIcon },
      // Conversations — WhatsApp. In "Sell" because it's the relationship channel: reps and
      // Operations both live here (Ops see all threads — they run the orders).
      { to: "/whatsapp", label: "Conversations", Icon: ChatIcon, badge: waWaiting },
    ].filter(Boolean) },
    { label: "Coach", items: [
      // While You Were Away lives INSIDE CallIQ, as a tab between Recordings and Insights
      // (Salem 2026-09-08) — a missed call is a call. So the badge rides on CallIQ rather
      // than on a nav entry of its own.
      { to: "/recordings", label: "CallIQ", Icon: PlayCircleIcon, badge: coverWaiting },
      // Trackers (review 2026-09-21, managers — §5.2) moved OUT of this top-level list and
      // INTO CallIQ as a tab, between Recordings and Missed & voicemail (Salem, 2026-09-25) —
      // see pages/CallIQHub.jsx. Route /trackers is unchanged (still ManagerArea-gated), so
      // nothing that links to it breaks; it's just reached via CallIQ now, not the nav.
      { to: "/trainiq", label: "The Academy", Icon: CapIcon },
      // The Games ("Theirs", 2026-10-07): the rep's own view of OG — standing, competitions,
      // badges. Reps and BCs only; managers run the games from /og at the top of the nav.
      !isOps && !isManagerOrAdmin && { to: "/games", label: "The Games", Icon: TrophyIcon },
      { to: "/board", label: "The Board", Icon: PinIcon },
      !isOps && { to: "/reports", label: "AI Reports", Icon: ReportsIcon },
    ].filter(Boolean) },
    { label: "Manage", items: [
      // People + the Admin Hub are ONE dashboard for managers now (Salem 2026-08-20,
      // decluttering the menu) — the Admin Hub is a tab inside /people. Non-managers keep
      // the standalone Admin Hub link at the bottom; it's where everyone books leave.
      isManagerOrAdmin && { to: "/people", label: "People & Admin", Icon: UsersIcon },
      // Toolkit Library moved into Settings → 🧰 Toolkit Library for admins (Salem
      // 2026-08-20); the /toolkit route stays in the nav for the people who actually
      // curate it — non-admin managers and the OPERATIONS team, who decide which
      // documents get uploaded and refreshed (Settings itself is admin-only).
      (isOps || (isManagerOrAdmin && !isAdmin))
        && { to: "/toolkit", label: "Toolkit Library", Icon: ToolboxIcon },
      // Intel Health moved into Settings → 🩺 Intel Health (Salem 2026-08-20); the nav
      // entry stays only for non-admin managers, since Settings is admin-only.
      isManagerOrAdmin && !isAdmin
        && { to: "/intelligence-health", label: "Intel Health", Icon: InsightsIcon },
      // RepIQ Scorecard (2026-10-15) — the quarterly review; same rule as Intel Health.
      isManagerOrAdmin && !isAdmin
        && { to: "/scorecard", label: "RepIQ Scorecard", Icon: InsightsIcon },
      // Signal Sources (review 2026-09-21, sources — section 4) moved into Settings -> Signal Sources
      // (Salem, 2026-09-25) - it's admin-only now, no nav entry here for non-admin managers.
    ].filter(Boolean) },
  ];

  const MenuIcon = ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
  const CloseIcon = ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );

  return (
    <aside className={"sidebar rq-side" + (sheetOpen ? " is-open" : "")} aria-label="Main">
      <NavLink to="/" className="sidebar-logo rq-side__brand" title="RepIQ">
        <span className="sidebar-logo-mark">
          {companyLogo
            ? <img src={companyLogo} alt="Company logo" className="sidebar-company-logo" style={{ width: "100%", height: "100%", objectFit: "contain", background: "#fff" /* a company logo sits on white in every theme (design system §1 exception) */, borderRadius: "inherit", padding: 3, boxSizing: "border-box" }} />
            : "IQ"}
        </span>
        <span className="sidebar-wordmark rq-side__wordmark">RepIQ</span>
      </NavLink>
      {/* Top-bar mode only (layout.css hides it otherwise): opens the nav as a sheet. */}
      <button
        ref={menuBtnRef}
        type="button"
        className="rq-side__menu"
        aria-label={sheetOpen ? "Close menu" : "Open menu"}
        aria-expanded={sheetOpen}
        aria-controls="rq-nav-sheet"
        onClick={() => setSheetOpen((o) => !o)}
      >
        {sheetOpen ? <CloseIcon /> : <MenuIcon />}
      </button>
      <div className="rq-side__sheet" id="rq-nav-sheet" ref={sheetRef}>
      <nav className="sidebar-nav rq-side__nav">
        {groups.map((g, i) => g.items.length > 0 && (
          <div className="sidebar-group" key={g.label || i}>
            {g.label && <div className="sidebar-group-label">{g.label}</div>}
            {g.items.map((it) => link(it.to, it.label, it.Icon, false, it.badge || 0))}
          </div>
        ))}
      </nav>
      <div className="sidebar-bottom">
        {/* The bell — in-app notifications (lead reminders, acceptance deadlines, …). */}
        <NotificationBell collapsed={collapsed} />
        {/* Everyone books leave here. Managers reach the same page as a tab inside
            People & Admin, so the standalone link only shows for everyone else. */}
        {!isManagerOrAdmin && link("/adminhub", "Admin Hub", CalendarIcon)}
        {user?.role === "admin" && link("/settings", "Settings", SettingsIcon)}
        <button
          className="sidebar-link sidebar-collapse-btn"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? "Expand" : "Collapse"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <ChevronLeftIcon size={18} />
          <span className="sidebar-label">Collapse</span>
        </button>
        <button
          className="sidebar-account"
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Account"
        >
          <Avatar name={user?.name} color={user?.avatar_color} size={30} photo={user?.photo} />
          <span className="sidebar-label">{user?.preferred_name || user?.name || "Account"}</span>
        </button>
      </div>
      {menuOpen && (
        <div className="user-pop" ref={popRef}>
          <div className="name">{user?.preferred_name || user?.name}</div>
          {user?.preferred_name && user?.preferred_name !== user?.name && (
            <div className="small muted" style={{ marginTop: -2 }}>{user?.name}</div>
          )}
          <div className="email">{user?.email}</div>
          <div className="small muted" style={{ marginBottom: 10, textTransform: "capitalize" }}>
            Role: {user?.role}
          </div>
          <div style={{ margin: "0 0 10px" }}>
            <div className="small muted" style={{ marginBottom: 4 }}>Theme</div>
            <select className="input" value={theme} onChange={(e) => changeTheme(e.target.value)}
              style={{ width: "100%" }}>
              {THEMES.map((t) => <option key={t.id} value={t.id}>{t.name} — {t.hint}</option>)}
            </select>
          </div>
          <div style={{ margin: "0 0 10px" }}>
            <div className="small muted" style={{ marginBottom: 4 }}>Display size</div>
            <select className="input" value={scale} onChange={(e) => changeScale(e.target.value)}
              style={{ width: "100%" }}>
              {SCALES.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div style={{ margin: "0 0 12px" }}>
            <div className="small muted" style={{ marginBottom: 2 }}>
              Menu background — <span style={{ fontWeight: 600 }}>{kitName}</span>
            </div>
            <div className="skin-grid">
              {KITS.map((k) => (
                <button
                  key={k.id}
                  type="button"
                  data-kit={k.id === "none" ? undefined : k.id}
                  className={"skin-chip" + (k.id === "none" ? " skin-chip-none" : "") + (kit === k.id ? " on" : "")}
                  title={`${k.name} — ${k.hint}`}
                  aria-label={k.name}
                  aria-pressed={kit === k.id}
                  onClick={() => changeKit(k.id)}
                >
                  {k.id === "none" ? "OFF" : null}
                </button>
              ))}
            </div>
          </div>
          <button className="btn btn-primary btn-sm" style={{ marginBottom: 8, width: "100%", justifyContent: "center" }}
            onClick={() => { setMenuOpen(false); navigate("/account"); }}>
            My profile &amp; account
          </button>
          {user?.id && (
            <button className="btn btn-outline btn-sm" style={{ marginBottom: 8, width: "100%", justifyContent: "center" }}
              onClick={() => { setMenuOpen(false); navigate(`/people/${user.id}`); }}>
              My HR record
            </button>
          )}
          <button className="btn btn-outline btn-sm" onClick={logout}>
            <LogoutIcon size={14} /> Sign out
          </button>
        </div>
      )}
      </div>
    </aside>
  );
}
